import React, { useReducer } from 'react';

import { BLIPP_FEE_PERCENT } from 'content/globalConstants';
import { ClientApiService } from 'services/ClientApiService';
import { Kyc } from 'components/Kyc';
import { companyKycQuestions } from 'utils/kycData';
import { getDataLayerEvents } from 'utils/getDatalayerEvents';

import InputSlider from './ui/InputSlider';
import Contact from './steps/Contact';
import Numbers from './steps/Numbers';
import Finish from './steps/Finish';
import styles from './CompanyCalculator.module.scss';

interface Errors {
  email?: string;
  phone_number?: string;
  car_number?: string;
  is_signatory?: string;
}

interface Props {
  totalCost?: number;
  licensePlate?: string;
}

const DEFAULT_PRICE = 250000;
const INTEREST = 9.0;
const CAR_NUMBER_REGEX = /[A-Za-z]{3}[0-9]{2}[A-Za-z0-9]{1}/g;

const limits = {
  price: [80000, 1200000],
  downPayment: [0, 50],
  period: [24, 48],
};

const residualPeriods = {
  24: [10, 65],
  36: [10, 55],
  48: [10, 40],
};

const numberToFormatedString = (number: number) =>
  String(number).replace(/(?!^)(?=(?:\d{3})+(?:\.|$))/gm, ' ');

const getResidualPeriod = (downPaymentPercent, period) => {
  const maxResidual = Math.floor(100 - downPaymentPercent);
  const residual = [
    residualPeriods[period][0],
    Math.floor(Math.min(maxResidual, residualPeriods[period][1]) / 5) * 5,
  ];

  return residual;
};
function calculateLoanMonthlyCost(
  rate = 0,
  periods = 0,
  present_value = 0, // credit amount
  future_value = 0, // residual value
  beginning = 1
) {
  // eslint-disable-next-line
  {
    const when = beginning ? 1 : 0;
    const customerRate = rate / 100;
    if (customerRate === 0) {
      return -(future_value + present_value) / periods;
    }

    return (
      // eslint-disable-next-line
      -(future_value + present_value * Math.pow(1 + customerRate, periods)) /
      // eslint-disable-next-line
      (((1 + customerRate * when) / customerRate) * (Math.pow(1 + customerRate, periods) - 1))
    );
  }
}

const reducer = (state: any, action: any) => {
  const { downPaymentPercent, period, residual, errors } = state;

  switch (action.type) {
    case 'loading':
      return { ...state, isLoading: action.value };
    case 'price':
      return {
        ...state,
        price: action.value,
        downPayment: Math.ceil((action.value * downPaymentPercent) / 100),
      };
    case 'downPayment': {
      const newResidualPeriod = getResidualPeriod(action.percent, period);

      return {
        ...state,
        downPayment: action.value,
        downPaymentPercent: action.percent,
        residual: residual > newResidualPeriod[1] ? newResidualPeriod[1] : residual,
        residualPeriod: newResidualPeriod,
      };
    }
    case 'period': {
      const newResidualPeriod = getResidualPeriod(downPaymentPercent, action.value);

      return {
        ...state,
        period: action.value,
        residual: residual > newResidualPeriod[1] ? newResidualPeriod[1] : residual,
        residualPeriod: newResidualPeriod,
      };
    }
    case 'residual':
      return { ...state, residual: action.value };
    case 'step':
      return { ...state, step: action.value, isLoading: false };

    case 'email':
    case 'phone_number':
    case 'car_number':
    case 'organisation_number':
    case 'is_signatory':
      return {
        ...state,
        [action.type]: action.value,
        errors: { ...errors, [action.type]: null },
      };
    case 'phone_number': // eslint-disable-line
      return {
        ...state,
        phone_number: action.value,
        errors: { ...errors, phone_number: null },
      };
    case 'error':
      return {
        ...state,
        errors: { ...errors, ...action.value },
        isLoading: false,
      };
    default:
      throw new Error('Någonting gick fel.');
  }
};

export const CompanySoftCalculator = ({ totalCost, licensePlate }: Props) => {
  const [
    {
      isLoading,
      price,
      downPayment,
      downPaymentPercent,
      period,
      residual,
      residualPeriod,
      email,
      phone_number, // eslint-disable-line
      car_number, // eslint-disable-line
      organisation_number, // eslint-disable-line
      is_signatory, // eslint-disable-line
      errors,
      step,
    },
    dispatch,
  ] = useReducer(reducer, {
    isLoading: false,
    price: totalCost || DEFAULT_PRICE,
    downPayment: 0,
    downPaymentPercent: 0,
    period: 36,
    residual: 55,
    residualPeriod: [10, 55],
    email: '',
    phone_number: '',
    car_number: licensePlate || '',
    organisation_number: '',
    is_signatory: false,
    errors: {},
    step: 0,
  });

  const priceWithBlippFee = price + (price * BLIPP_FEE_PERCENT) / 100;

  const handleInitial = () => {
    dispatch({ type: 'step', value: step + 1 });
  };
  const handlePriceChange = (value: any) => {
    if (!value || value < limits.price[0]) return;

    dispatch({ type: 'price', value });
  };

  const handleDownPaymentChange = (src: any, value: any) => {
    const setDownPayment = (
      { value, percent } // eslint-disable-line
    ) => dispatch({ type: 'downPayment', value, percent });

    if (src === 'input') {
      if (value > price / 2) setDownPayment({ value: price / 2, percent: 50 });
      else if (value < 0) setDownPayment({ value: 0, percent: 0 });
      else
        setDownPayment({
          value,
          percent: Math.ceil((value * 100) / price),
        });
    } else {
      setDownPayment({
        value: Math.ceil((price * value) / 100),
        percent: value,
      });
    }
  };

  const handlePeriodInputChange = (value: any) => {
    if (value >= 24) {
      const periods = [24, 36, 48];
      let min = {
        value: 48,
        index: 0,
      };
      // eslint-disable-next-line
      periods.forEach((period, index) => {
        const localMin = value - period;
        if (localMin >= 0 && localMin < min.value)
          min = {
            value: localMin,
            index,
          };
      });

      dispatch({ type: 'period', value: periods[min.index] });
    }
  };

  const handleResidualChange = (value: any) => {
    if (value >= 10) dispatch({ type: 'residual', value: Math.round(value / 5) * 5 });
  };

  const handleContactSubmit = async () => {
    // const apiService = new ApiService();

    const newErrors: Errors = {};
    let hasErros = false;

    if (!email) {
      newErrors.email = 'Ange din e-post';
      hasErros = true;
    }

    if (!phone_number) {
      newErrors.phone_number = 'Ange ditt telefonnummer';
      hasErros = true;
    }

    if (hasErros) {
      dispatch({ type: 'error', value: newErrors });
      return;
    }

    try {
      dispatch({ type: 'loading', value: true });

      // await apiService.post('/users/soft-signup', { email, phone_number });
      const method = 'postContact';
      const payload = { email, phone: phone_number, source: 'nordealeasing' };
      const response = await ClientApiService.proxyRequest({ method, payload });
      const responseJson = await response.json();

      if (!response.ok) {
        const responseErrors = {};
        responseJson.body.errors.forEach((err: any) => (responseErrors[err.field] = err.message)); // eslint-disable-line
        dispatch({ type: 'error', value: responseErrors });

        throw new Error(`Request failed with status ${response.status}`);
      } else {
        dispatch({ type: 'step', value: step + 1 });
      }
    } catch (error: any) {
      if (Array.isArray(error.errors)) {
        const responseErrors = {};
        error.forEach((err: any) => (responseErrors[err.field] = err.message)); // eslint-disable-line
        dispatch({ type: 'error', value: responseErrors });
      }
    }
  };

  const handleNumbersSubmit = async () => {
    const newErrors: any = {};
    let hasErrors = false;
    if (!organisation_number) {
      hasErrors = true;
      newErrors.organisation_number = 'Ange ett organisationsnummer';
    }

    if (car_number && !car_number.match(CAR_NUMBER_REGEX)) {
      hasErrors = true;
      newErrors.car_number = 'Ange ett giltigt nummer';
    }

    if (hasErrors) {
      dispatch({ type: 'error', value: newErrors });
      return;
    }
    dispatch({ type: 'step', value: step + 1 });
  };

  const handleKycSubmit = async (values: any) => {
    const kycValuesAndQuestions = companyKycQuestions.reduce((acc, question) => {
      // eslint-disable-next-line
      acc[question.name] = {
        question: question.question,
        answer: values[question.name],
      };
      return acc;
    }, {});

    const newErrors: any = {};
    let hasErrors = false;

    if (!is_signatory) {
      hasErrors = true;
      newErrors.is_signatory = 'Du måste acceptera villkoren för att ansöka';
    }

    if (hasErrors) {
      dispatch({ type: 'error', value: newErrors });
      return;
    }
    // eslint-disable-next-line
    await handleFormSubmit(kycValuesAndQuestions);
  };

  const handleFormSubmit = async (kycValues: any) => {
    dispatch({ type: 'loading', value: true });
    const method = 'postLeasing';
    const payload = {
      amount: price,
      months: period,
      down_payment: downPayment,
      residual_percent: residual,
      monthly_cost: Math.round(
        calculateLoanMonthlyCost(
          INTEREST / 12,
          period,
          -(priceWithBlippFee - downPayment) * 0.8,
          (priceWithBlippFee * 0.8 * residual) / 100
        )
      ).toFixed(2),
      interest_rate: INTEREST,
      registration_number: car_number,
      blipp_fee_percent: BLIPP_FEE_PERCENT,
      amount_with_blipp_fee: priceWithBlippFee,
      loan_type: 'nordealeasing',
      company: {
        organisation_number: organisation_number ? organisation_number.toString() : '',
        email,
        phone_number,
      },
      additional_data: kycValues,
      terms_and_conditions: true,
    };

    const response = await ClientApiService.proxyRequest({ method, payload });
    const responseJson = await response.json();
    if (!response.ok) {
      const responseErrors = {};
      responseJson.body.errors.forEach((err: any) => (responseErrors[err.field] = err.message)); // eslint-disable-line
      dispatch({
        type: 'error',
        value: { is_signatory: responseJson.body.errors[0] },
      });
    } else {
      dispatch({ type: 'step', value: step + 1 });
      // Send conversion event to GTM
      getDataLayerEvents('leasing-application-submitted');
    }
  };

  return step === 4 ? (
    <Finish />
  ) : (
    <div className={styles.root}>
      <InputSlider
        className="mb-2"
        size="large"
        min={limits.price[0]}
        max={limits.price[1]}
        value={price}
        step={10000}
        // @ts-ignore
        onChange={handlePriceChange}
        suffix=" kr"
        label="Bilens pris inkl. moms"
        inputProps={{
          min: 0,
        }}
      />
      <div className={styles.distance} />
      <InputSlider
        className="mb-2"
        size="large"
        min={0}
        max={50}
        limits={['0 %', '50 %']}
        value={downPaymentPercent}
        // @ts-ignore
        onChange={(value) => handleDownPaymentChange('slider', value)}
        suffix=" kr"
        label="Första förhöjd avgift"
        inputProps={{
          value: downPayment,
          onChange: (value) => handleDownPaymentChange('input', value),
          max: price / 2,
        }}
      />
      <div className={styles.distance} />
      <InputSlider
        className="mb-2"
        size="small"
        min={limits.period[0]}
        max={limits.period[1]}
        inputWidth={120}
        value={period}
        step={12}
        // @ts-ignore
        onChange={(value) => dispatch({ type: 'period', value })}
        suffix=" mån"
        label="Leasingtid"
        inputProps={{
          onChange: handlePeriodInputChange,
          min: 0,
        }}
      />
      <div className={styles.distance} />
      <InputSlider
        className="mb-2"
        size="small"
        min={residualPeriod[0]}
        max={residualPeriod[1]}
        value={residual}
        step={5}
        // @ts-ignore
        onChange={handleResidualChange}
        suffix=" %"
        label="Restvärde"
        inputProps={{
          min: 0,
        }}
      />
      <div className={styles.distance} />
      <div className={styles.bottomWrapper}>
        <div className={styles.monthlyCost}>
          <span>Månadskostnad exkl. moms</span>
          <h2>
            {numberToFormatedString(
              Math.round(
                calculateLoanMonthlyCost(
                  INTEREST / 12,
                  period,
                  -(priceWithBlippFee - downPayment) * 0.8,
                  (priceWithBlippFee * 0.8 * residual) / 100
                )
              )
            )}{' '}
            kr/mån
          </h2>
          <small>Att använda Blipp kostar {BLIPP_FEE_PERCENT}% av bilens pris</small>
        </div>
        <div className={styles.form}>
          {step === 0 && (
            <button type="button" className={styles.initial} onClick={handleInitial}>
              Påbörja ansökan
            </button>
          )}
          {step === 1 && (
            <Contact
              email={email}
              onEmailChange={(value: any) => dispatch({ type: 'email', value })}
              phone_number={phone_number}
              onPhoneChange={(value: any) => dispatch({ type: 'phone_number', value })}
              handleSubmit={handleContactSubmit}
              errors={errors}
            />
          )}
          {step === 2 && (
            <Numbers
              car_number={car_number}
              onCarNumberChange={(value: any) => dispatch({ type: 'car_number', value })}
              organisation_number={organisation_number}
              onOrganisationNumberChange={(value: any) =>
                dispatch({ type: 'organisation_number', value })
              }
              handleSubmit={handleNumbersSubmit}
              errors={errors}
            />
          )}
          {step === 3 && (
            <Kyc
              is_signatory={is_signatory}
              onIsSignatoryChange={(value: any) => dispatch({ type: 'is_signatory', value })}
              onSubmit={handleKycSubmit}
              errors={errors}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};
